
import { GooglePlacesSelectValue } from "@/types/google-places";
import { BookingFormData } from "@/components/ParcelBookingForm";
import { useGooglePlacesService } from "./useGooglePlacesService";

interface AddressPlaceHandlersProps {
  formData: BookingFormData;
  setFormData: React.Dispatch<React.SetStateAction<BookingFormData>>;
  validateAddresses: (collection: any, delivery: any) => boolean;
}

export const useAddressPlaceHandlers = ({ 
  formData, 
  setFormData, 
  validateAddresses 
}: AddressPlaceHandlersProps) => {
  const { getPlaceDetails, extractAddressComponents, fetchSavedAddress } = useGooglePlacesService();

  // UUID pattern to properly distinguish saved addresses from Google Places IDs
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const handleCollectionSelect = async (place: GooglePlacesSelectValue) => {
    console.log('Selected Place Data:', place);
    
    // If this is from saved addresses (place_id is a UUID), use database data
    if (uuidPattern.test(place.value.place_id)) {
      console.log('Using saved address for collection address');
      try {
        const savedAddress = await fetchSavedAddress(place.value.place_id);
        const updatedAddress = {
          ...formData.collectionAddress,
          street: savedAddress.street,
          street2: savedAddress.street2 || '',
          city: savedAddress.city,
          province: savedAddress.province,
          postalCode: savedAddress.postal_code,
          country: savedAddress.country,
        };
        setFormData((prev) => ({
          ...prev,
          collectionAddress: updatedAddress,
        }));
        validateAddresses(updatedAddress, formData.deliveryAddress);
      } catch (error) {
        console.error('Error fetching saved address:', error);
      }
      return;
    }

    try {
      const placeDetails = await getPlaceDetails(place.value.place_id);
      const components = extractAddressComponents(placeDetails.address_components);
      
      const streetAddress = [
        components.streetNumber,
        components.street,
        components.sublocality
      ].filter(Boolean).join(' ');

      const updatedAddress = {
        ...formData.collectionAddress,
        street: streetAddress,
        city: components.city,
        province: components.province,
        postalCode: components.postalCode,
        country: components.country,
      };

      setFormData((prev) => ({
        ...prev,
        collectionAddress: updatedAddress,
      }));
      validateAddresses(updatedAddress, formData.deliveryAddress);
    } catch (error) {
      console.error('Error fetching place details:', error);
      if (error.message !== 'Not a Google Places ID') {
        console.error('Unexpected error:', error);
      }
    }
  };

  const handleDeliverySelect = async (place: GooglePlacesSelectValue) => {
    console.log('Selected Place Data:', place);
    
    // If this is from saved addresses (place_id is a UUID), use database data
    if (uuidPattern.test(place.value.place_id)) {
      console.log('Using saved address for delivery address');
      try {
        const savedAddress = await fetchSavedAddress(place.value.place_id);
        const updatedAddress = {
          ...formData.deliveryAddress,
          street: savedAddress.street,
          street2: savedAddress.street2 || '',
          city: savedAddress.city,
          province: savedAddress.province,
          postalCode: savedAddress.postal_code,
          country: savedAddress.country,
        };
        setFormData((prev) => ({
          ...prev,
          deliveryAddress: updatedAddress,
        }));
        validateAddresses(formData.collectionAddress, updatedAddress);
      } catch (error) {
        console.error('Error fetching saved address:', error);
      }
      return;
    }

    try {
      const placeDetails = await getPlaceDetails(place.value.place_id);
      const components = extractAddressComponents(placeDetails.address_components);
      
      const streetAddress = [
        components.streetNumber,
        components.street,
        components.sublocality
      ].filter(Boolean).join(' ');

      const updatedAddress = {
        ...formData.deliveryAddress,
        street: streetAddress,
        city: components.city,
        province: components.province,
        postalCode: components.postalCode,
        country: components.country,
      };

      setFormData((prev) => ({
        ...prev,
        deliveryAddress: updatedAddress,
      }));
      validateAddresses(formData.collectionAddress, updatedAddress);
    } catch (error) {
      console.error('Error fetching place details:', error);
      if (error.message !== 'Not a Google Places ID') {
        console.error('Unexpected error:', error);
      }
    }
  };

  return {
    handleCollectionSelect,
    handleDeliverySelect,
  };
};
